import { LightningElement, api, track, wire } from "lwc";
import classNames from "classnames";

import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { getCurrentInteractionMethod } from "common/interactionMethodObserver";
import { getNextFocusIndex } from "common/a11y";
import { DEFAULT_SPRITE, Sprite } from "tds/icon";
import { BooleanAttr, attrToBool, flattenOptions } from "common/helpers";
import { pointsForRect } from "common/geometry";
import { ResizeAdapter } from "common/resizeAdapter";

import * as locales from "./locales";

type ComboboxVariant = "base" | "readonly" | "accordion";

// Offset to account for border + margin + padding when positioning fixed menu.
const FIXED_MENU_OFFSET = 14;

export interface ComboboxOptionInit {
  label: string;
  value: string;
  allSelected?: boolean;
  icon?: {
    symbol: string;
    sprite?: Sprite;
  };
  options?: ComboboxOptionInit[];
}

export interface ComboboxOption extends ComboboxOptionInit {
  className: string;
  focused: boolean;
  hidden: boolean;
  checkbox: boolean;
  selected: boolean;
  onclick: (e: PointerEvent) => void;
  onmouseover: () => void;
}

// Remove whitespace from a string for autocomplete matching
const trimString = (str: string) => str.replace(/\s|\n/g, "");

export default class TdsInput extends LightningElement {
  @api variant: ComboboxVariant = "base";
  @api label: string | null = null;
  @api placeholder: string | null = null;
  @api busy: BooleanAttr = false;
  @api toggleDisabled: BooleanAttr = false;
  @api fixedMenu: BooleanAttr = false;
  @api ariaLabel: string | null = null;
  @api disabled: BooleanAttr = false;
  @api required: string | null = null;
  @api errorMessage: string | null = null;

  // Ensure unique id's for <input>.
  private static elementCount = -1;

  @track private selection: string[] = [];
  @track private _options: ComboboxOption[] = [];

  @api
  get options() {
    return this.toOptions(this._options);
  }

  set options(newValue: ComboboxOption[]) {
    this._options = newValue;
  }

  @api
  get value() {
    if (!this.selection.length) return "";
    return this.multiple ? this.selection : this.selection[0];
  }

  set value(newValue: string | string[]) {
    // Add new value to selection set
    if (newValue == null) {
      this.selection = [];
    } else {
      this.selection = ([] as string[]).concat(newValue);
    }
  }

  private _multiple: boolean = false;
  private _selectAllEnabled: boolean = false;

  @api
  get multiple() {
    return this._multiple;
  }

  set multiple(newValue) {
    this._multiple = attrToBool(newValue);
  }

  @api
  get selectAllEnabled() {
    return this._selectAllEnabled;
  }

  set selectAllEnabled(newValue) {
    this._selectAllEnabled = attrToBool(newValue);
  }

  get renderSelectAll() {
    return this._selectAllEnabled && this.inputValue == "";
  }

  @api
  focus() {
    this.template.querySelector("input")?.focus();
  }

  @wire(LabelsAdapter, { locales, options: "$labelOptions" })
  private labels: LabelsAdapterValue = null!;

  private get inputId() {
    return `combobox-input__${TdsInput.elementCount}`;
  }

  private get controlClass() {
    return classNames("control", {
      "control--disabled": this.isDisabled,
      "combobox--errored": this.hasError,
    });
  }

  private get isDisabled() {
    return attrToBool(this.disabled);
  }

  private get labelClasses() {
    return classNames({ "label--required": this.isRequired });
  }

  private get isRequired() {
    return attrToBool(this.required);
  }

  private get labelOptions() {
    const activeOption = this.visibleOptions[this.focusedIndex] || {
      label: "",
    };

    return {
      aria_set: {
        current_index: this.focusedIndex + 1,
        total_option_count: this.flatOptions.length,
        visible_option_count: this.visibleOptions.length,
      },
      aria_option_deselected: {
        label: activeOption.label,
      },
      aria_option_focused: {
        label: activeOption.label,
      },
      aria_option_selected: {
        label: activeOption.label,
      },
      selected: {
        count: this.selection.length,
      },
    };
  }

  connectedCallback() {
    TdsInput.elementCount++;
    document.addEventListener("click", this.handleDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.handleDocumentClick);
  }

  private lastEventType: "input" | "focus" | "select" | "deselect" | null =
    null;
  private inputValue: string = "";
  private isOpen: boolean = false;
  private hasFocus: boolean = false;
  private focusedValue: string | null = null;
  private _focusedIndex: number = 0;
  private openTop = false;

  private get inputIcon() {
    const option = this.options.find((o) => o.value === this.value);
    return option && option.icon;
  }

  private get focusedIndex() {
    return this._focusedIndex;
  }

  private set focusedIndex(newValue) {
    this._focusedIndex = newValue;
    this.lastEventType = "focus";

    if (this.visibleOptions.length) {
      this.focusedValue = this.visibleOptions[newValue].value;
    }
  }

  private get focusedElementId() {
    if (this.template.querySelector(".option--focused")) {
      return this.visibleOptions[this.focusedIndex]?.value ?? null;
    } else {
      return null;
    }
  }

  private get visibleOptions() {
    return this.flatOptions.filter((o) => !o.hidden);
  }

  private get flatOptions() {
    return flattenOptions(this.options);
  }

  private get flatOptionInits() {
    return flattenOptions(this._options);
  }

  private get className() {
    return classNames("combobox", `combobox--${this.variant}`, {
      "combobox--disabled": this.isDisabled,
      "combobox--null": !this.value,
      "combobox--focused": this.hasFocus,
    });
  }

  private get isBusy() {
    return attrToBool(this.busy);
  }

  private get isToggleable() {
    return !attrToBool(this.toggleDisabled);
  }

  private get isFixedMenu() {
    return attrToBool(this.fixedMenu);
  }

  private get isInputReadOnly() {
    return ["readonly", "accordion"].includes(this.variant);
  }

  // https://bugs.chromium.org/p/chromium/issues/detail?id=914451
  // https://bugs.chromium.org/p/chromium/issues/detail?id=587466
  // https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion#preventing_autofilling_with_autocompletenew-password
  private get autoCompleteAttribute() {
    return "off";
  }

  private get ariaAutoCompleteAttribute() {
    return !this.isInputReadOnly ? "list" : null;
  }

  private get inputStyle() {
    return this.isOpen || (this.hasFocus && !this.value)
      ? "z-index: 1;"
      : "z-index: 0;";
  }

  private get displayValue() {
    if (!this.value) {
      return this.placeholder || this.labels.placeholder;
    }

    if (this.multiple) {
      return this.labels.selected;
    }

    const option = this.flatOptions.find((o) => o.value == this.value);

    return option ? option.label : this.placeholder || this.labels.placeholder;
  }

  private get hasNoMatches() {
    return this.inputValue && !this.visibleOptions.length;
  }

  private get shouldDeferClose() {
    return this.variant === "accordion";
  }

  private get controlTabIndex() {
    return this.isInputReadOnly ? "0" : "-1";
  }

  private get ariaLiveMessage() {
    switch (this.lastEventType) {
      case "focus":
        return `${this.labels.aria_option_focused} ${this.labels.aria_set}`;
      case "select":
        return `${this.labels.aria_option_selected} ${this.labels.aria_set}`;
      case "deselect":
        return `${this.labels.aria_option_deselected} ${this.labels.aria_set}`;
      case "input":
        return !this.visibleOptions.length
          ? this.labels.no_matches
          : this.displayValue;
      default:
        return this.displayValue || null;
    }
  }

  private get ariaLabelledby() {
    return this.label ? "label" : null;
  }

  private get listboxClass() {
    return classNames("listbox", {
      "listbox--top": this.openTop,
      "listbox--fixed": this.isFixedMenu,
      "listbox--hidden": this.variant == "readonly",
    });
  }

  private checkAllSelected(options: ComboboxOptionInit[]) {
    if (!this.selection.length) return false;
    const optionValues = options.map((option) => option.value);
    return optionValues.every((value) => this.selection.includes(value));
  }

  private toOptions(optionInits: ComboboxOptionInit[]): ComboboxOption[] {
    return optionInits.map((o) => {
      const focused = o.value === this.focusedValue;
      const selected = this.selection.includes(o.value);
      const checkbox = this.multiple;
      const isHidden = (option: ComboboxOptionInit) => {
        const input = trimString(this.inputValue.toLowerCase());
        const test = trimString((option.label || option.value).toLowerCase());
        return test.indexOf(input) === -1;
      };
      const allSelected =
        this.selectAllEnabled && o.options
          ? this.checkAllSelected(o.options)
          : false;

      return {
        label: o.label,
        value: o.value,
        icon: o.icon && { ...o.icon, sprite: o.icon.sprite || DEFAULT_SPRITE },
        options: o.options ? this.toOptions(o.options) : undefined,
        className: classNames("option", {
          "option--focused": focused,
          "option--selected": selected,
          "option--checkbox": checkbox,
        }),
        focused,
        hidden: o.options ? o.options.every(isHidden) : isHidden(o),
        checkbox,
        selected,
        text: o.label,
        allSelected: allSelected,
        toggleSelectAll: (e: PointerEvent) => {
          e.preventDefault();
          // Handles adding and removing from this.selection
          o.options?.forEach((option) => {
            if (allSelected || !this.selection.includes(option.value)) {
              this.selectOption(option.value);
            }
          });
        },
        onclick: (e: PointerEvent) => {
          // Prevent multiple click handler calls when target is checkbox.
          e.preventDefault();
          // Get the target index from the current set of visible options
          this.focusedIndex = this.visibleOptions.findIndex(
            ({ value }) => value === o.value
          );
          this.selectOption(o.value);
        },
        onmouseover: () => {
          if (getCurrentInteractionMethod("control") === "mouse") {
            // Get the target index from the current set of visible options
            this.focusedIndex = this.visibleOptions.findIndex(
              ({ value }) => value === o.value
            );
          }
        },
      };
    });
  }

  private focusInput() {
    const control = this.isInputReadOnly
      ? this.template.querySelector(".control")
      : this.template.querySelector("input");

    if (control) {
      control.focus();
    }
  }

  private blurInput() {
    const input = this.template.querySelector("input");

    if (input) {
      input.blur();
    }
  }

  private get listbox() {
    return this.template.querySelector('[role="listbox"]')!;
  }

  private scrollOptionIntoView(index: number) {
    const options = this.template.querySelectorAll('[role="option"]');
    const option = options[index] as HTMLElement;

    if (!option) return;

    const listbox = this.listbox;
    const shouldScroll =
      option.offsetTop > listbox.clientHeight - option.clientHeight ||
      option.offsetTop < listbox.scrollTop - listbox.clientHeight;

    if (shouldScroll) {
      const { paddingTop, paddingBottom } = getComputedStyle(listbox);
      listbox.scrollTop =
        option.offsetTop -
        listbox.clientHeight +
        option.clientHeight +
        parseInt(paddingTop) +
        parseInt(paddingBottom);
    }
  }

  private focusFirstOption() {
    const selected = this.selection[0];

    // Focus the first selected option OR the first option if none are selected
    if (selected) {
      const selectedIndex = this.visibleOptions.findIndex(
        (o) => o.value === selected
      );
      this.focusedIndex = Math.max(selectedIndex, 0);
    } else {
      // Keep focus on current item unless it becomes hidden, then focus the very first item
      this.focusedIndex = Math.max(
        0,
        this.visibleOptions.findIndex((o) => o.value === this.focusedValue)
      );
    }

    // Scroll into view
    this.scrollOptionIntoView(this.focusedIndex);
  }

  /**
   * Sets size, position, etc for combobox menu if applicable.
   */
  private setMenuStyle(rect = this.template.host.getBoundingClientRect()) {
    // Fixed menu positioning accounts for menus inside fixed positioned
    // elements like modals. In this case, we need to calculate the menu's
    // size and position since it is removed from the normal DOM flow
    // and is not contained by its parent.
    if (this.isFixedMenu) {
      this.openTop = pointsForRect(rect).topLeft.isBottom;

      const style = {
        bottom: "",
        top: "",
        maxHeight: "",
        width: `${rect.width}px`,
        left: `${rect.left}px`,
      };

      if (this.openTop) {
        // Position menu at top of combobox.
        const bottom = window.innerHeight - rect.y;
        style.bottom = `${bottom}px`;
        style.maxHeight = `${
          window.innerHeight - (bottom + FIXED_MENU_OFFSET)
        }px`;
      } else {
        // Position menu at bottom of combobox.
        const top = rect.y + rect.height;
        style.top = `${top}px`;
        style.maxHeight = `${window.innerHeight - (top + FIXED_MENU_OFFSET)}px`;
      }

      Object.assign(this.listbox.style, style);
    }
  }

  @wire(ResizeAdapter)
  private onResize() {
    if (this.isOpen) {
      window.requestAnimationFrame(() => this.setMenuStyle());
    }
  }

  private openListBox() {
    if (this.isOpen || this.isDisabled) return;

    const rect = this.template.host.getBoundingClientRect();
    this.openTop = pointsForRect(rect).topLeft.isBottom;
    this.setMenuStyle(rect);
    this.isOpen = true;

    if (this.visibleOptions.length) {
      this.focusFirstOption();
    }
  }

  private closeListBox() {
    this.isOpen = false;
    this.inputValue = "";
    this.lastEventType = null;
  }

  private selectOption(value: string) {
    const flatOptions = this.flatOptions;
    const option = flatOptions.find((o) => o.value === value);

    if (!option) return;

    if (this.selection.includes(option.value) && this.isToggleable) {
      this.selection = this.selection.filter((s) => s !== option.value);
      this.lastEventType = "deselect";
    } else {
      if (!this.multiple) {
        this.selection = [];
      }
      this.selection.push(option.value);
      this.lastEventType = "select";
    }

    this.value = this.selection;
    this.dispatchEvent(new CustomEvent("change", { detail: this.value }));

    if (!this.shouldDeferClose && !this.multiple) {
      this.dispatchEvent(new CustomEvent("close"));
      this.closeListBox();
    }
  }

  private handleDocumentClick = (e: MouseEvent) => {
    const isInsideClick = !!e
      .composedPath()
      .find((t) => t === this.template.host);

    if (!this.shouldDeferClose && !isInsideClick && this.isOpen) {
      this.closeListBox();
      this.dispatchEvent(new CustomEvent("close"));
    }
  };

  private handleControlMouseDown(e: MouseEvent) {
    e.preventDefault();

    if (!this.hasFocus && !this.isOpen) {
      this.focusInput();
      this.openListBox();
    } else if (!this.isOpen) {
      this.openListBox();
    } else {
      this.closeListBox();
      this.dispatchEvent(new CustomEvent("close"));
    }
  }

  private handleInputChange(e: { target: HTMLInputElement }) {
    this.openListBox();
    this.inputValue = e.target.value;

    if (this.visibleOptions.length) {
      this.focusFirstOption();
    } else {
      this.lastEventType = "input";
    }
  }

  private handleInputFocus() {
    this.hasFocus = true;
  }

  private handleInputBlur() {
    this.hasFocus = false;
    if (!this.shouldDeferClose && !this.multiple) {
      this.closeListBox();
    }
  }

  private handleListboxMouseDown(e: MouseEvent) {
    // Only handle left-clicks
    if (e.button !== 0) return;

    e.stopPropagation();
    e.preventDefault();

    this.focusInput();
  }

  private handleKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case "Enter":
        e.preventDefault();

        if (!this.isOpen) {
          this.openListBox();
        } else {
          this.selectOption(this.visibleOptions[this.focusedIndex].value);
        }

        break;
      case " ":
        // Only handle spaces if the user hasn't started typing
        if (this.inputValue) break;

        e.preventDefault();

        if (!this.isOpen) {
          this.openListBox();
        } else if (!this.inputValue) {
          this.selectOption(this.visibleOptions[this.focusedIndex].value);
        }

        break;
      case "Escape":
        if (this.isOpen) {
          e.preventDefault();
          this.closeListBox();
          this.dispatchEvent(new CustomEvent("close"));
        }

        break;
      case "ArrowUp": {
        e.preventDefault();

        this.focusedIndex = getNextFocusIndex(
          "prev",
          this.focusedIndex,
          this.visibleOptions.length
        );

        // Scroll into view
        this.scrollOptionIntoView(this.focusedIndex);

        break;
      }
      case "ArrowDown": {
        e.preventDefault();

        if (!this.isOpen) {
          this.openListBox();
        } else {
          this.focusedIndex = getNextFocusIndex(
            "next",
            this.focusedIndex,
            this.visibleOptions.length
          );
        }

        // Scroll into view
        this.scrollOptionIntoView(this.focusedIndex);

        break;
      }
      case "Tab": {
        if (this.isOpen) {
          e.preventDefault();
          this.closeListBox();
        }
        break;
      }
      default:
        break;
    }
  }

  private get hasError() {
    return !!this.errorMessage;
  }
}
